import { CommonModule } from '@angular/common';
import { Component, signal, HostListener, input, output, ChangeDetectionStrategy, computed } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { FormField, SelectOption } from '../../../../../models/form.models';

@Component({
  selector: 'app-form-field',
  standalone: true,
  imports: [CommonModule, TranslatePipe],
  templateUrl: './form-field.component.html',
  styleUrl: './form-field.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FormFieldComponent {
  field = input.required<FormField>();
  options = input.required<SelectOption[]>();
  valueChange = output<{
    fieldId: string;
    value: string;
  }>();
  customValueChange = output<{
    fieldId: string;
    customValue: string;
  }>();

  isDropdownOpen = signal<boolean>(false);

  // Pre-compute translation key to avoid calling method in template
  translationKey = computed<string>(() => {
    const field = this.field();
    const value = field.value;
    if (value === 'other') {
      return `app.components.courseCreator.settings.mainInfo.${field.id}.options.other`;
    }
    const option = this.options().find((opt) => opt.value === value);
    return option
      ? `app.components.courseCreator.settings.mainInfo.${field.id}.options.${option.translationKey}`
      : '';
  });

  // Pre-compute if "other" is selected
  isOtherSelected = computed<boolean>(() => {
    return this.field().value === 'other';
  });

  toggleDropdown(): void {
    this.isDropdownOpen.set(!this.isDropdownOpen());
  }

  closeDropdown(): void {
    this.isDropdownOpen.set(false);
  }

  onSelectionChange(value: string): void {
    this.valueChange.emit({ fieldId: this.field()?.id, value });
    this.closeDropdown();
  }

  onCustomValueInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.customValueChange.emit({
      fieldId: this.field().id ?? '',
      customValue: target.value,
    });
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;

    if (!target) {
      return;
    }

    // Check if click is outside the dropdown wrapper
    const clickedInsideDropdown = target.closest(
      '.form-field__dropdown-wrapper'
    );

    // If clicked outside the dropdown wrapper, close it
    if (!clickedInsideDropdown && this.isDropdownOpen()) {
      this.closeDropdown();
    }
  }
}
